import React, { useCallback, useEffect, useState, FormEvent } from 'react';
import axiosInstance from 'config/axiosInstance';
import Pagination from 'reusable/pagination';
import SortBy from 'reusable/sortby';
import { IconEdit } from '@tabler/icons-react';
import EditDialog from 'reusable/Dialog/editDialog';
import {
  DialogConfig,
  DialogField,
  DialogType,
  NewDataField,
} from 'reusable/Dialog/dialogInterface';
import Search from 'reusable/search';
import DateRangeFilter from 'reusable/datefilterrange';
import { PAGE_SIZE } from 'config/constant';
import { useTranslation } from 'react-i18next';

const sortingOptions = [
  { label: 'Default', value: '' },
  { label: 'Most Recent', value: 'start_time' },
];

interface DriverWorkingRecordTableProps {
  id?: string;
}

interface DriverWorkingRecord {
  id: string;
  start_time: string;
  stop_time: string;
  client_delivery: string;
  task: string;
}

interface ClientTaskNames {
  clientName: string;
  taskName: string;
}

const DriverWorkingRecordTable: React.FC<DriverWorkingRecordTableProps> = ({
  id,
}) => {
  const { t, i18n } = useTranslation('driver');
  const [currentPage, setCurrentPage] = useState(1);
  const [sortValue, setSortValue] = useState('');
  const [count, setCount] = useState(0);
  const [driverWorkingRecord, setDriverWorkingRecord] = useState<
    DriverWorkingRecord[]
  >([]);
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [searchValue, setSearchValue] = useState('');
  const [clientTaskNames, setClientTaskNames] = useState<ClientTaskNames[]>([]);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [dialogProps, setDialogProps] = useState<DialogConfig>({
    title: '',
    subtitle: '',
    fields: [],
  });
  const [currentWorkingRecordId, setCurrentWorkingRecordId] = useState<
    string | null
  >(null);

  const handleSortChange = (sortValue: string) => {
    setSortValue(sortValue);
    setCurrentPage(1);
  };

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setDateRange({ startDate, endDate });
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    setCurrentPage(1);
  };

  const formatDate = (datetime: string) => {
    if (!datetime) {
      return '-';
    }
    const date = new Date(datetime);
    const months = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec',
    ];
    const day = date.getDate();
    const monthIndex = date.getMonth();
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');

    if (i18n.language === 'jp') {
      return `${year}/${(monthIndex + 1).toString().padStart(2, '0')}/${day
        .toString()
        .padStart(2, '0')} | ${hours}:${minutes}`;
    } else {
      return `${day < 10 ? '0' + day : day} ${
        months[monthIndex]
      } ${year} | ${hours}:${minutes}`;
    }
  };

  const fetchClientAndTaskNames = useCallback(
    async (records: DriverWorkingRecord[]) => {
      try {
        const clientTaskNamesPromises = records.map(async (record) => {
          const clientPromise = axiosInstance.get(
            `/v1/client_deliveries/${record.client_delivery}/`
          );
          const taskPromise = axiosInstance.get(`/v1/tasks/${record.task}/`);

          const [clientResponse, taskResponse] = await Promise.all([
            clientPromise,
            taskPromise,
          ]);

          return {
            clientName: clientResponse.data.name,
            taskName: taskResponse.data.name,
          };
        });

        const names = await Promise.all(clientTaskNamesPromises);
        setClientTaskNames(names);
      } catch (error) {
        console.error('Failed to fetch client and task names', error);
      }
    },
    []
  );

  const fetchData = useCallback(async () => {
    try {
      let apiUrl = '/v1/task_sessions/?';
      if (dateRange.startDate && dateRange.endDate) {
        apiUrl += `start_time__gte=${dateRange.startDate}T00:00:00.000Z&start_time__lte=${dateRange.endDate}T23:59:59.999Z&`;
      }
      apiUrl += `order_by=${sortValue}&search=${searchValue}&page=${currentPage}&page_size=${PAGE_SIZE}`;
      const response = await axiosInstance.get(apiUrl);
      setDriverWorkingRecord(response.data.results);
      setCount(response.data.count);

      await fetchClientAndTaskNames(response.data.results);
    } catch (error) {
      console.error('Failed to fetch data', error);
    }
  }, [fetchClientAndTaskNames, sortValue, searchValue, dateRange, currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const createEditDialogFields = (
    record: DriverWorkingRecord
  ): DialogField[] => {
    const addHours = (dateString: string, hours: number) => {
      const date = new Date(dateString);
      date.setHours(date.getHours() + hours);
      return date;
    };

    const startDate = record.start_time
      ? addHours(record.start_time, 9).toISOString().slice(0, 10)
      : '';
    const startTime = record.start_time
      ? addHours(record.start_time, 9).toISOString().slice(11, 16)
      : '';
    const endDate = record.stop_time
      ? addHours(record.stop_time, 9).toISOString().slice(0, 10)
      : '';
    const endTime = record.stop_time
      ? addHours(record.stop_time, 9).toISOString().slice(11, 16)
      : '';

    return [
      {
        label: 'Client Name',
        name: 'clientName',
        value: clientTaskNames.find((c) => c.clientName)?.clientName || '',
      },
      {
        label: 'Task Name',
        name: 'taskName',
        value: clientTaskNames.find((t) => t.taskName)?.taskName || '',
      },
      {
        label: 'Start Date',
        name: 'startDate',
        value: startDate,
        type: 'date',
      },
      {
        label: 'Start Time',
        name: 'startTime',
        value: startTime,
        type: 'time',
      },
      { label: 'End Date', name: 'endDate', value: endDate, type: 'date' },
      { label: 'End Time', name: 'endTime', value: endTime, type: 'time' },
    ];
  };

  const openDialog = (
    title: string,
    subtitle: string,
    fields: DialogField[],
    dialogType: DialogType
  ) => {
    setDialogProps({ title, subtitle, fields });
    if (dialogType === DialogType.Edit) setIsEditDialogOpen(true);
  };

  const closeDialog = () => {
    setIsEditDialogOpen(false);
    setCurrentWorkingRecordId(null);
  };

  const handleDialog = async (
    dialogType: DialogType,
    record: DriverWorkingRecord
  ) => {
    console.log(record);
    if (dialogType === DialogType.Edit) {
      setCurrentWorkingRecordId(record.id);
      openDialog(
        'Edit Working Record',
        '',
        createEditDialogFields(record),
        DialogType.Edit
      );
    }
  };

  const handleEditWorkingRecord = async (
    e: FormEvent<Element>,
    updatedData: NewDataField & { id: string }
  ) => {
    console.log(updatedData);
    e.preventDefault();

    const combineDateTime = (date: any, time: any): string => {
      const combinedDateTime = new Date(`${date}T${time}:00.000Z`);
      return combinedDateTime.toISOString();
    };

    const startDateTime = combineDateTime(
      updatedData.startDate,
      updatedData.startTime
    );
    const endDateTime = combineDateTime(
      updatedData.endDate,
      updatedData.endTime
    );

    const formattedUpdatedData = {
      start_time: startDateTime,
      stop_time: endDateTime,
    };

    try {
      if (updatedData) {
        const response = await axiosInstance.patch(
          `/v1/task_sessions/${updatedData.id}/`,
          formattedUpdatedData
        );

        setDriverWorkingRecord((prevData) =>
          prevData.map((record) =>
            record.id === updatedData.id ? response.data : record
          )
        );
      }

      setIsEditDialogOpen(false);
    } catch (error) {
      console.error('Failed to update working record:', error);
    }
  };
  console.log('Driver working record:', driverWorkingRecord);
  return (
    <section className="flex flex-col space-y-2">
      <div className="flex flex-row space-x-2 items-center justify-between">
        <div className="flex w-2/5 justify-start">
          <Search
            placeholder="Client Name, Task Name"
            onSearchChange={handleSearchChange}
          />
        </div>
        <div className="flex flex-row gap-2">
          <SortBy
            options={sortingOptions}
            defaultValue={sortValue}
            onChange={handleSortChange}
          />
          <DateRangeFilter onDateRangeChange={handleDateRangeChange} />
        </div>
      </div>

      <div>
        <div className="flex flex-row bg-primary/50 rounded-md py-2 px-4">
          <h6 className="flex-1 text-center">
            {t('view.working-record.client-name')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.working-record.task-name')}
          </h6>
          <h6 className="flex-1 text-center">
            {t('view.working-record.start')}
          </h6>
          <h6 className="flex-1 text-center">{t('view.working-record.end')}</h6>
          <h6 className="flex-1 text-center">
            {t('view.working-record.action')}
          </h6>
        </div>
        <div>
          {driverWorkingRecord.map((record, index) => (
            <div
              key={index}
              className="grid grid-cols-5 gap-4 px-4 py-3 border-b items-center"
            >
              <div className="flex justify-center">
                {clientTaskNames[index]?.clientName || '-'}
              </div>
              <div className="flex justify-center">
                {clientTaskNames[index]?.taskName || '-'}
              </div>
              <div className="flex justify-center">
                {formatDate(record.start_time)}
              </div>
              <div className="flex justify-center">
                {formatDate(record.stop_time)}
              </div>
              <div className="flex justify-center">
                <IconEdit
                  size={20}
                  stroke={1.5}
                  className="cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDialog(DialogType.Edit, record);
                  }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      <EditDialog
        isOpen={isEditDialogOpen}
        onClose={closeDialog}
        handleSubmit={(e: any, updatedData: any) =>
          handleEditWorkingRecord(e, {
            ...updatedData,
            id: currentWorkingRecordId ?? '',
          })
        }
        confirmLabel={t('edit.save')}
        cancelLabel={t('edit.cancel')}
        title={dialogProps.title}
        subtitle={dialogProps.subtitle ?? ''}
        fields={dialogProps.fields}
        taskSessionId={currentWorkingRecordId ?? ''}
      />

      <div>
        <Pagination
          allRows={count}
          rowsPerPage={PAGE_SIZE}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
        />
      </div>
    </section>
  );
};

export default DriverWorkingRecordTable;
